import moment from "moment";
import { SHIFT_STATUS } from "Constants/constant";
import { capitalizeAllLetters, parseBooleanVal } from "Utils/utils";

/** True for shifts created via V5 advance mode (`is_multi_assignment === true`). */
export const isAdvanceMultiAssignmentShift = (shift) =>
  parseBooleanVal(shift?.is_multi_assignment);

/**
 * Same wall-clock start/end on consecutive days = 24-hour shift (basic or advance).
 * Used for calendar labels (e.g. midnight shows as 12:00 am, not 00:00 AM).
 */
export const isCalendar24HourShift = (shift) => {
  const start = moment(shift?.start_time);
  const end = moment(shift?.end_time);
  if (!start.isValid() || !end.isValid()) return false;

  if (start.format("HH:mm") !== end.format("HH:mm")) return false;

  const diffMinutes = end.diff(start, "minutes");
  return diffMinutes >= 1439;
};

/** @deprecated Prefer isCalendar24HourShift — kept for advance-only call sites. */
export const isAdvance24HourShift = (shift) =>
  isAdvanceMultiAssignmentShift(shift) && isCalendar24HourShift(shift);

/** 12-hour range label for 24-hour shifts; null → use legacy calendar formatting. */
export const formatAdvance24HourShiftCalendarTimeRange = (shift) => {
  if (!isCalendar24HourShift(shift)) return null;

  const start = moment(shift?.start_time);
  const end = moment(shift?.end_time);
  const startLabel = start.format("hh:mm A");
  const endLabel = end.isValid() ? end.format("hh:mm A") : "-";
  return `${startLabel} - ${endLabel}`;
};

/** Calendar card time range — always 12-hour clock with uppercase AM/PM. */
export const getShiftCalendarTimeRangeLabel = (shift) => {
  const fullDayLabel = formatAdvance24HourShiftCalendarTimeRange(shift);
  if (fullDayLabel) return fullDayLabel;

  const start = moment(shift?.start_time);
  const end = moment(shift?.end_time);
  if (!start.isValid()) return "";

  return `${start.format("hh:mm A")} - ${
    end.isValid() ? end.format("hh:mm A") : ""
  }`;
};

/**
 * Assignment slot times are Laravel `datetime` casts (ISO 8601 with Z/offset).
 * parseZone keeps the stored wall-clock instead of shifting into the browser TZ
 * (e.g. 04:00Z must stay 4:00 AM, not 9:00 AM in UTC+5).
 */
export const formatAdvanceSlotWallClockTime = (time) => {
  if (!time) return "";
  const parsed = moment.parseZone(time);
  return parsed.isValid() ? parsed.format("hh:mm A") : String(time);
};

/** Hover label for a V5 open worker slot, including overnight (+1 day). */
export const formatAdvanceOpenSlotTimeRange = (entry) => {
  if (!entry?.start_time && !entry?.end_time) return "Unassigned";

  const startLabel = formatAdvanceSlotWallClockTime(entry?.start_time);
  const endLabel = formatAdvanceSlotWallClockTime(entry?.end_time);
  if (!endLabel) return startLabel || "Unassigned";
  if (!startLabel) return endLabel;

  const start = moment.parseZone(entry.start_time);
  const end = moment.parseZone(entry.end_time);
  const isNextDay =
    start.isValid() &&
    end.isValid() &&
    end.format("YYYY-MM-DD") !== start.format("YYYY-MM-DD");

  return `${startLabel} – ${endLabel}${isNextDay ? " (+1 day)" : ""}`;
};

export const getAdvanceShiftWorkers = (shift) => {
  if (Array.isArray(shift?.workers) && shift.workers.length > 0) {
    return shift.workers;
  }

  const assignments =
    shift?.worker_assignments ?? shift?.workerAssignments ?? [];
  if (!Array.isArray(assignments) || assignments.length === 0) {
    return [];
  }

  return assignments.map((assignment) => ({
    assignment_id: assignment?.id ?? assignment?.assignment_id ?? null,
    is_open:
      parseBooleanVal(assignment?.is_open) ||
      assignment?.worker_id == null ||
      !assignment?.worker,
    status: assignment?.status ?? "accepted",
    progress: assignment?.progress ?? "created",
    start_time: assignment?.start_time ?? null,
    end_time: assignment?.end_time ?? null,
    worker: assignment?.worker ?? null,
    worker_id: assignment?.worker_id ?? assignment?.worker?.id ?? null,
  }));
};

const countAdvanceOpenSlots = (shift, v5Workers = []) => {
  if (shift?.open_slots_count != null) {
    return Number(shift.open_slots_count) || 0;
  }
  return v5Workers.filter(
    (entry) => parseBooleanVal(entry?.is_open) || !entry?.worker
  ).length;
};

const countAdvanceFilledSlots = (shift, v5Workers = []) => {
  if (shift?.filled_slots_count != null) {
    return Number(shift.filled_slots_count) || 0;
  }
  return v5Workers.filter((entry) => entry?.worker).length;
};

/** Vacant worker assignment rows on a V5 advance shift. */
export const getAdvanceOpenSlotEntries = (shift) =>
  getAdvanceShiftWorkers(shift).filter(
    (entry) => parseBooleanVal(entry?.is_open) || !entry?.worker
  );

/** Assigned worker rows on a V5 advance shift. */
export const getAdvanceFilledWorkerEntries = (shift) =>
  getAdvanceShiftWorkers(shift).filter((entry) => entry?.worker);

const normalizeAvatarPerson = (person) => {
  if (!person || typeof person !== "object") return null;
  return {
    ...person,
    first_name: person?.first_name,
    last_name: person?.last_name,
    photo: person?.photo ?? person?.profile_picture,
    profile_picture: person?.profile_picture ?? person?.photo,
  };
};

const dedupeAvatarPeople = (people = []) => {
  const seen = new Set();
  return people.filter((person) => {
    const key = person?.id ?? person?.uuid;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getAdvanceShiftPresentation = (shift) => {
  const isV5 = isAdvanceMultiAssignmentShift(shift);
  const v5Workers = getAdvanceShiftWorkers(shift);
  const participants = shift?.participants || [];
  const participantCount = participants.length;
  const openSlotCount = countAdvanceOpenSlots(shift, v5Workers);
  const filledSlotCount = countAdvanceFilledSlots(shift, v5Workers);
  const workerCount = filledSlotCount;

  if (!isV5) {
    const legacyGroup =
      participantCount > 1 || parseBooleanVal(shift?.is_group_shift);
    return {
      isV5: false,
      isLegacy: true,
      isV5Filled: false,
      isV5Open: false,
      isV5Pending: false,
      isV5PartialOpen: false,
      isV5SlotOpenOnly: false,
      isOpenShift: false,
      isOpenGroupShift: parseBooleanVal(shift?.open_group_shift),
      isPendingWorkers: false,
      isGroupShift: legacyGroup,
      isAdvanceGroupShift: false,
      workerCount: shift?.worker ? 1 : 0,
      participantCount,
      openSlotCount: 0,
      filledSlotCount: 0,
      v5Workers: [],
    };
  }

  const isV5SlotOpenOnly =
    parseBooleanVal(shift?.is_slot_open_only) ||
    (openSlotCount > 0 && filledSlotCount === 0);
  const isV5PartialOpen =
    parseBooleanVal(shift?.is_partial_open) ||
    (openSlotCount > 0 && filledSlotCount > 0);
  const isV5Open =
    (parseBooleanVal(shift?.is_open_shift) && v5Workers.length === 0) ||
    isV5SlotOpenOnly;
  const isV5Pending =
    !parseBooleanVal(shift?.is_open_shift) &&
    v5Workers.length === 0 &&
    !isV5SlotOpenOnly;
  const isV5Filled = filledSlotCount > 0 && openSlotCount === 0;
  // All V5 multi-assignment shifts use the Group Shift calendar card
  // (green accent + "X Wkr"/"X Pt" chips), including 1:1 participant/worker.
  const isAdvanceGroupShift = true;

  return {
    isV5: true,
    isLegacy: false,
    isV5Filled,
    isV5Open,
    isV5Pending,
    isV5PartialOpen,
    isV5SlotOpenOnly,
    isOpenShift: isV5Open,
    isOpenGroupShift:
      isV5Open &&
      (parseBooleanVal(shift?.open_group_shift) || participantCount > 1),
    isPendingWorkers: isV5Pending,
    isGroupShift: isAdvanceGroupShift,
    isAdvanceGroupShift,
    workerCount,
    participantCount,
    openSlotCount,
    filledSlotCount,
    v5Workers,
  };
};

export const getAdvanceWorkerDisplayLabel = (shift) => {
  const { isV5, v5Workers } = getAdvanceShiftPresentation(shift);
  if (!isV5 || !v5Workers.length) return null;

  const names = v5Workers
    .map((entry) => entry?.worker)
    .filter(Boolean)
    .map((w) =>
      `${capitalizeAllLetters(w.first_name || "")} ${capitalizeAllLetters(
        w.last_name || ""
      )}`.trim()
    )
    .filter(Boolean);

  if (!names.length) return null;
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
};

/** Simple calendar label, e.g. "2 participants, 1 worker, 1 open slot". */
export const getAdvanceShiftCountLabel = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  if (!presentation.isV5) return null;

  const parts = [];
  if (presentation.participantCount > 0) {
    parts.push(
      `${presentation.participantCount} participant${
        presentation.participantCount === 1 ? "" : "s"
      }`
    );
  }
  if (presentation.workerCount > 0) {
    parts.push(
      `${presentation.workerCount} worker${
        presentation.workerCount === 1 ? "" : "s"
      }`
    );
  }
  if (presentation.openSlotCount > 0) {
    parts.push(
      `${presentation.openSlotCount} open slot${
        presentation.openSlotCount === 1 ? "" : "s"
      }`
    );
  }
  return parts.length ? parts.join(", ") : null;
};

/**
 * Display title for filled advance (V5) shifts on the schedule calendar.
 * 1 worker + 1 participant → "Advance Shift"; otherwise "Advance Group Shift".
 */
export const getAdvanceShiftDisplayLabel = (shiftOrPresentation) => {
  const presentation =
    shiftOrPresentation?.isV5 !== undefined &&
    shiftOrPresentation?.workerCount !== undefined
      ? shiftOrPresentation
      : getAdvanceShiftPresentation(shiftOrPresentation);

  if (!presentation?.isV5) return null;

  const workerCount = presentation.workerCount ?? 0;
  const participantCount = presentation.participantCount ?? 0;

  return workerCount > 1 || participantCount > 1
    ? "Advance Group Shift"
    : "Advance Shift";
};

/** Compact stat chips for advance group shift calendar cards and popovers. */
export const getAdvanceGroupShiftStatChips = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  if (!presentation.isV5 || !presentation.isAdvanceGroupShift) return [];

  const chips = [];

  if (presentation.participantCount > 0) {
    chips.push({
      key: "participants",
      short: `${presentation.participantCount} Pt`,
      label: `${presentation.participantCount} participant${
        presentation.participantCount === 1 ? "" : "s"
      }`,
    });
  }

  const workerCount = presentation.filledSlotCount || presentation.workerCount;
  if (workerCount > 0) {
    chips.push({
      key: "workers",
      short: `${workerCount} Wkr`,
      label: `${workerCount} worker${workerCount === 1 ? "" : "s"}`,
    });
  }

  if (presentation.openSlotCount > 0) {
    chips.push({
      key: "open",
      short: `${presentation.openSlotCount} Open`,
      label: `${presentation.openSlotCount} open slot${
        presentation.openSlotCount === 1 ? "" : "s"
      }`,
      variant: "amber",
    });
  }

  return chips;
};

export const isAdvanceGroupShiftCard = (
  shift,
  { isV5FilledAdvance = false, isV5PartialOpen = false } = {}
) => {
  const presentation = getAdvanceShiftPresentation(shift);
  return (
    presentation.isV5 &&
    presentation.isAdvanceGroupShift &&
    (isV5FilledAdvance || isV5PartialOpen)
  );
};

export const getAdvancePartialOpenBadgeLabel = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  if (!presentation.isV5PartialOpen || !presentation.openSlotCount) {
    return null;
  }
  return `${presentation.openSlotCount} open`;
};

/**
 * Display-only progress for V5 multi-assignment shifts (open slots ignored).
 * Prefers backend `display_status`; falls back to computing from workers[].
 *
 * @returns {'partial_in_progress'|'in_progress'|'completed'|null}
 */
export const resolveAdvanceDisplayStatus = (shift) => {
  if (!isAdvanceMultiAssignmentShift(shift)) {
    return null;
  }

  const backendStatus = String(shift?.display_status || "")
    .trim()
    .toLowerCase();
  if (
    backendStatus === "partial_in_progress" ||
    backendStatus === "in_progress" ||
    backendStatus === "completed"
  ) {
    return backendStatus;
  }

  if (parseBooleanVal(shift?.is_partial_in_progress)) {
    return "partial_in_progress";
  }
  if (parseBooleanVal(shift?.is_fully_in_progress)) {
    return "in_progress";
  }

  const filled = getAdvanceFilledWorkerEntries(shift);
  if (!filled.length) {
    return null;
  }

  const startedCount = filled.filter(
    (entry) => String(entry?.progress || "").toLowerCase() === "started"
  ).length;
  const completedCount = filled.filter((entry) =>
    ["submitted", "pay_approved"].includes(
      String(entry?.progress || "").toLowerCase()
    )
  ).length;

  if (completedCount === filled.length) {
    return "completed";
  }
  if (startedCount === filled.length) {
    return "in_progress";
  }
  if (startedCount > 0) {
    return "partial_in_progress";
  }

  return null;
};

const hasNonEmptyShiftValue = (value) =>
  value != null && String(value).trim() !== "";

/**
 * True when the shift belongs to a recurrence series (root or occurrence).
 * Empty-array / missing detail payloads must not count as recurring.
 */
export const isAdvanceShiftInRecurringSeries = (shift) => {
  if (!shift || Array.isArray(shift)) return false;
  return (
    hasNonEmptyShiftValue(shift.recurring_interval) ||
    hasNonEmptyShiftValue(shift.recurred_shift_id)
  );
};

/**
 * Whether the Date field on the advance edit form should be locked.
 * Accepted and completed shifts stay editable; in-progress, recurring, and
 * invoiced shifts stay locked.
 */
export const isAdvanceShiftDateChangeLocked = (
  shift,
  { isInvoiced = false } = {}
) => {
  if (!shift || Array.isArray(shift)) return false;
  if (isInvoiced) return true;
  if (isAdvanceShiftInRecurringSeries(shift)) return true;

  const displayStatus = resolveAdvanceDisplayStatus(shift);
  if (
    displayStatus === "in_progress" ||
    displayStatus === "partial_in_progress"
  ) {
    return true;
  }

  if (String(shift.progress || "").toLowerCase() === "started") {
    return true;
  }

  const assignments = [
    ...(Array.isArray(shift.workers) ? shift.workers : []),
    ...(Array.isArray(shift.worker_assignments) ? shift.worker_assignments : []),
    ...(Array.isArray(shift.workerAssignments) ? shift.workerAssignments : []),
  ];

  return assignments.some(
    (assignment) =>
      String(assignment?.progress || "").toLowerCase() === "started"
  );
};

/** Calendar / popover label for V5 display-only progress; null → use legacy status label. */
export const getAdvanceDisplayStatusLabel = (shift) => {
  switch (resolveAdvanceDisplayStatus(shift)) {
    case "partial_in_progress":
      return "Partial in Progress";
    case "in_progress":
      return "In-Progress";
    case "completed":
      return "Completed";
    default:
      // Staffing, not progress: a vacant slot outranks the container's DB status,
      // which stays `pending` on advance shifts even once slots are published.
      return getAdvanceShiftPresentation(shift).isV5PartialOpen
        ? "Partial Open"
        : null;
  }
};

export const getAdvancePartialOpenTooltip = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  if (!presentation.isV5PartialOpen) return null;
  const { filledSlotCount, openSlotCount } = presentation;
  const total = filledSlotCount + openSlotCount;
  return `Partially staffed — ${filledSlotCount} of ${total} worker slot${
    total === 1 ? "" : "s"
  } filled`;
};

export const getAdvanceShiftParticipantsForAvatar = (shift, rowParticipant = null) => {
  const participants = dedupeAvatarPeople(
    (shift?.participants || [])
      .map(normalizeAvatarPerson)
      .filter(Boolean)
  );

  if (participants.length > 0) {
    return participants;
  }

  const singleParticipant = normalizeAvatarPerson(
    shift?.participant ?? rowParticipant
  );
  return singleParticipant ? [singleParticipant] : [];
};

/**
 * Per-worker assignment status for advance hover avatars.
 * Progress wins over container status so Partial In Progress can show mixed tags.
 */
export const getAdvanceWorkerAssignmentStatusMeta = (entry) => {
  if (!entry || typeof entry !== "object") return null;

  const progress = String(entry?.progress || "")
    .trim()
    .toLowerCase();
  const status = String(entry?.status || "").trim();
  const statusKey = status.toLowerCase();
  const shiftAccept = parseBooleanVal(entry?.shift_accept);

  if (progress === "started" || statusKey === "n/a") {
    return {
      label: "In-Progress",
      short: "IP",
      statusKey: SHIFT_STATUS.NA,
      progress: "started",
      shiftAccept,
    };
  }
  if (progress === "submitted" || progress === "pay_approved") {
    return {
      label: "Completed",
      short: "Done",
      statusKey: SHIFT_STATUS.ACCEPTED,
      progress: "submitted",
      shiftAccept,
    };
  }
  if (statusKey === SHIFT_STATUS.REJECTED) {
    return {
      label: "Rejected",
      short: "Rej",
      statusKey: SHIFT_STATUS.REJECTED,
      progress,
      shiftAccept,
    };
  }
  if (statusKey === SHIFT_STATUS.EXPIRED) {
    return {
      label: "Expired",
      short: "Exp",
      statusKey: SHIFT_STATUS.EXPIRED,
      progress,
      shiftAccept,
    };
  }
  if (statusKey === SHIFT_STATUS.DID_NOT_ACCEPT) {
    return {
      label: "Did not accept",
      short: "DNA",
      statusKey: SHIFT_STATUS.DID_NOT_ACCEPT,
      progress,
      shiftAccept,
    };
  }
  if (statusKey === SHIFT_STATUS.PENDING) {
    return {
      label: "Pending",
      short: "Pend",
      statusKey: SHIFT_STATUS.PENDING,
      progress,
      shiftAccept,
    };
  }
  if (statusKey === SHIFT_STATUS.PUBLISHED) {
    if (shiftAccept) {
      return {
        label: "Pending",
        short: "Pend",
        statusKey: SHIFT_STATUS.PUBLISHED,
        progress,
        shiftAccept: true,
      };
    }
    return {
      label: "Accepted",
      short: "Acc",
      statusKey: SHIFT_STATUS.PUBLISHED,
      progress,
      shiftAccept: false,
    };
  }
  if (
    statusKey === SHIFT_STATUS.ACCEPTED ||
    statusKey === SHIFT_STATUS.APPROVED
  ) {
    return {
      label: "Accepted",
      short: "Acc",
      statusKey: SHIFT_STATUS.ACCEPTED,
      progress,
      shiftAccept,
    };
  }
  if (status || progress) {
    return {
      label: status || progress,
      short: String(status || progress)
        .slice(0, 4)
        .replace(/^\w/, (c) => c.toUpperCase()),
      statusKey: status || SHIFT_STATUS.PENDING,
      progress,
      shiftAccept,
    };
  }
  return null;
};

const attachWorkerAssignmentStatus = (person, entry) => {
  if (!person) return null;
  return {
    ...person,
    assignmentStatus: getAdvanceWorkerAssignmentStatusMeta(entry),
  };
};

export const getAdvanceShiftWorkersForAvatar = (shift, rowWorker = null) => {
  const workers = dedupeAvatarPeople(
    getAdvanceFilledWorkerEntries(shift)
      .map((entry) =>
        attachWorkerAssignmentStatus(
          normalizeAvatarPerson(entry?.worker),
          entry
        )
      )
      .filter(Boolean)
  );

  if (workers.length > 0) {
    return workers;
  }

  const singleWorker = attachWorkerAssignmentStatus(
    normalizeAvatarPerson(shift?.worker ?? rowWorker),
    {
      status: shift?.status,
      progress: shift?.progress,
      shift_accept: shift?.shift_accept,
    }
  );
  return singleWorker ? [singleWorker] : [];
};

/** Calendar row IDs to attach a V5 shift after create/update. Returns null → use legacy logic. */
export const getAdvanceShiftCalendarTargetIds = (shift, targetKey) => {
  if (!isAdvanceMultiAssignmentShift(shift)) return null;

  const presentation = getAdvanceShiftPresentation(shift);

  if (targetKey === "participant") {
    const ids = [];
    (shift.participants || []).forEach((p) => {
      if (p?.id) ids.push(p.id);
    });
    if (!ids.length && shift.pt_id) ids.push(shift.pt_id);
    return [...new Set(ids)];
  }

  if (targetKey === "worker") {
    if (presentation.isV5Open || presentation.isV5Pending) {
      return [];
    }
    const ids = [];
    getAdvanceFilledWorkerEntries(shift).forEach((entry) => {
      const id = entry?.worker?.id ?? entry?.worker_id;
      if (id) ids.push(id);
    });
    if (!ids.length && shift.worker_id) ids.push(shift.worker_id);
    if (!ids.length && shift.worker?.id) ids.push(shift.worker.id);
    return [...new Set(ids)];
  }

  return [];
};

export const isAdvanceGroupShiftForAvatar = (shift) => {
  const presentation = getAdvanceShiftPresentation(shift);
  return presentation.isV5 && presentation.isAdvanceGroupShift;
};
